import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BarChart3, CalendarCheck, Clock, Heart, Sparkles, Users } from "lucide-react";
import { SITE, scrollToId } from "@/config/site";
import { PlayButton } from "./PlayButton";

const LINES = [
  { text: "Build the Practice", tone: "dark" },
  { text: "You Dreamed Of.", tone: "dark" },
  { text: "And Live the Life", tone: "light" },
  { text: "You Deserve.", tone: "light" },
];

const TRUST = [
  { icon: BarChart3, label: "Greater Clarity" },
  { icon: Clock, label: "Less Effort" },
  { icon: Users, label: "More Time for What Matters" },
  { icon: Heart, label: "A More Fulfilling Life" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative overflow-hidden pb-16 pt-28 sm:pt-32 lg:pb-24 lg:pt-40">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-brand-mint blur-3xl" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:px-8">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand-mint px-4 py-2"
            data-testid="hero-badge"
          >
            <Sparkles className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
            <span className="text-xs font-semibold tracking-wide text-brand-ink">Built by Dentists. For Real Dental Practice.</span>
          </motion.div>

          <h1 data-testid="hero-headline" className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.tone === "dark" ? "text-brand-ink" : "font-light text-slate-400"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            DENSA helps you manage your dental practice with greater clarity and less effort, so you can build the
            practice you&apos;ve dreamed of and live the life you deserve—with more time for family, travel, dentistry,
            experiences, memories, and yourself.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <PlayButton testid="hero-download-google-play-btn" />
            <button
              type="button"
              data-testid="hero-explore-features-btn"
              onClick={() => scrollToId("#features")}
              className="group inline-flex min-h-[52px] items-center gap-2 rounded-full border border-brand/30 bg-white px-6 py-3 text-sm font-semibold text-brand-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-brand hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            >
              Explore Features
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-12 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-slate-200 pt-8 sm:grid-cols-4"
            data-testid="hero-trust-strip"
          >
            {TRUST.map(({ icon: Icon, label }) => (
              <li key={label} className="flex flex-col gap-2">
                <Icon className="h-5 w-5 text-brand" aria-hidden="true" />
                <span className="text-xs font-medium leading-snug text-slate-600">{label}</span>
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-brand/20"
          >
            <motion.img
              src={SITE.assets.hero}
              alt="A dentist balancing clinic life and family life — the DENSA promise"
              data-testid="hero-image"
              style={{ y: imgY, scale: 1.12 }}
              className="aspect-[3/4] w-full object-cover sm:aspect-[4/4.4]"
              loading="eager"
            />
            <div className="pointer-events-none absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/20" aria-hidden="true" />
          </motion.div>

          <motion.div
            style={{ y: cardY }}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -left-3 top-8 sm:-left-8"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center gap-3 rounded-2xl border border-white/60 bg-white/90 px-4 py-3 shadow-xl backdrop-blur-md"
              data-testid="hero-badge-appointments"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-mint">
                <CalendarCheck className="h-4.5 w-4.5 h-5 w-5 text-brand" aria-hidden="true" />
              </span>
              <span className="text-xs font-semibold text-brand-ink">
                Today&apos;s schedule
                <span className="block text-[11px] font-normal text-slate-500">Appointments, follow-ups &amp; lab work</span>
              </span>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -right-2 bottom-10 sm:-right-6"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center gap-3 rounded-2xl border border-white/60 bg-white/90 px-4 py-3 shadow-xl backdrop-blur-md"
              data-testid="hero-badge-diva"
            >
              <img src={SITE.assets.appIcon} alt="DENSA app icon" className="h-9 w-9 rounded-xl" />
              <span className="text-xs font-semibold text-brand-ink">
                Ask DIVA
                <span className="block text-[11px] font-normal text-slate-500">Insights from your clinic data</span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
